import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminPortalLayout from '@/modules/admin/components/AdminPortalLayout';
import { useToast } from '@/hooks/useToast';
import { useAdminApps } from '@/hooks/queries';

export default function AdminAppsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast, toasts, hideToast } = useToast();
  
  const urlFilter = searchParams.get('filter');
  const filter = (urlFilter === 'live') ? 'live' : 'all';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const urlSearch = searchParams.get('search') || '';
  
  const [searchQuery, setSearchQuery] = useState(urlSearch);

  const { data, isLoading: loading } = useAdminApps(page, 50, filter, urlSearch);

  const apps = data?.apps || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (filter !== 'all') {
      params.append('filter', filter);
    }
    if (searchQuery.trim()) {
      params.append('search', searchQuery.trim());
    }
    params.append('page', '1'); // Reset to first page on new search
    navigate(`/admin-portal/apps?${params.toString()}`);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams();
    if (filter !== 'all') {
      params.append('filter', filter);
    }
    if (searchQuery.trim()) {
      params.append('search', searchQuery.trim());
    }
    params.append('page', newPage.toString());
    navigate(`/admin-portal/apps?${params.toString()}`);
  };

  const handleFilterChange = (newFilter) => {
    const params = new URLSearchParams();
    if (newFilter !== 'all') {
      params.append('filter', newFilter);
    }
    if (searchQuery.trim()) {
      params.append('search', searchQuery.trim());
    }
    params.append('page', '1'); // Reset to first page on filter change
    navigate(`/admin-portal/apps?${params.toString()}`);
  };

  const getStatusBadge = (status) => {
    const badges = {
      live: { bg: 'bg-green-100', text: 'text-green-700', label: 'Live', icon: '✅' },
      draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft', icon: '📋' },
      archived: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Archived', icon: '📦' },
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <span>{badge.icon}</span>
        <span>{badge.label}</span>
      </span>
    );
  };

  const getRequestStatusBadge = (status) => {
    const badges = {
      open: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Open', icon: '📬' },
      quoted: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Quoted', icon: '📋' },
      in_progress: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'In Progress', icon: '🔨' },
      awaiting_approval: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Awaiting Approval', icon: '👀' },
    };
    const badge = badges[status] || badges.open;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <span>{badge.icon}</span>
        <span>{badge.label}</span>
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-zapt-purple mb-4"></div>
            <p className="text-gray-600">Loading apps...</p>
          </div>
        </div>
      </AdminPortalLayout>
    );
  }

  return (
    <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">
              📱 Admin: Apps
            </h1>
          </div>
          <p className="text-gray-600">
            View and manage all apps ({pagination.total} total)
          </p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Filter Tabs */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  filter === 'all'
                    ? 'bg-zapt-purple text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Apps ({pagination.total})
              </button>
              <button
                onClick={() => handleFilterChange('live')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  filter === 'live'
                    ? 'bg-zapt-purple text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Live Apps
              </button>
            </div>

            {/* Search Form */}
            <form onSubmit={handleSearchSubmit} className="flex-1 flex gap-2">
              <input
                type="text"
                placeholder="Search apps by name, description, or user email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <button
                type="submit"
                className="px-6 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 transition-colors font-medium text-sm whitespace-nowrap"
              >
                Search
              </button>
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    const params = new URLSearchParams();
                    if (filter !== 'all') {
                      params.append('filter', filter);
                    }
                    params.append('page', '1');
                    navigate(`/admin-portal/apps?${params.toString()}`);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm whitespace-nowrap"
                >
                  Clear
                </button>
              )}
            </form>
          </div>
        </div>

        {/* Apps List */}
        {apps.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchQuery ? 'No apps match your search.' : 'No apps found.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 mb-6">
              {apps.map((app) => (
              <div
                key={app.id}
                onClick={() => navigate(`/admin-portal/app/${app.id}`)}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900">{app.name}</h3>
                      {getStatusBadge(app.status)}
                      {app.approvalStatus === 'pending_approval' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          ⏳ Awaiting Approval
                        </span>
                      )}
                      {app.approvalStatus === 'approved' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          ✅ Approved - Ready for Production
                        </span>
                      )}
                      {app.hasActiveRequest && app.activeRequest && (
                        getRequestStatusBadge(app.activeRequest.status)
                      )}
                      {app.hasNotes && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          💬 {app.noteCount} {app.noteCount === 1 ? 'note' : 'notes'}
                        </span>
                      )}
                      {app.hasUnpaidCharges && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          💳 ${app.unpaidChargesAmount.toFixed(2)} unpaid
                        </span>
                      )}
                      {app.tags && app.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {app.tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                          {app.tags.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              +{app.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {app.description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{app.description}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {app.appUrl && (
                        <a
                          href={app.appUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-zapt-purple hover:text-zapt-purple/80"
                        >
                          View Live →
                        </a>
                      )}
                      {app.devUrl && (
                        <a
                          href={app.devUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          View Dev →
                        </a>
                      )}
                      {app.updatedAt && (
                        <span>Updated {formatDate(app.updatedAt)}</span>
                      )}
                    </div>

                    {app.hasActiveRequest && app.activeRequest && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Active Request:</span> {app.activeRequest.title}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="ml-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin-portal/app/${app.id}`);
                      }}
                      className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 transition-colors font-medium text-sm"
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              </div>
            ))}
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} apps
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.hasPrevPage}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                        pagination.hasPrevPage
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                              pagination.page === pageNum
                                ? 'bg-zapt-purple text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.hasNextPage}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                        pagination.hasNextPage
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
    </AdminPortalLayout>
  );
}

